import { useMemo } from "react";

import { useCustomMaterials } from "#modules/materials/view/useCustomMaterials";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import MaterialChip from "#modules/quoteRequestLines/view/MaterialChip";
import { PartTypeChip } from "#modules/quoteRequestLines/view/PartTypeChip";
import { DimensionChip } from "#shared/view/components/DimensionChip";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { WeightChip } from "#shared/view/components/WeightChip";

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  part: PartDto;
  hasChildren: boolean;
  disablePartTypeChip?: boolean;
  onSubmitPartDimensions?: () => void;
  isConsumable: boolean;
  direction?: "horizontal" | "vertical";
  showWeight: boolean;
  showMaterial?: boolean;
};

export const QuoteRequestLineChips = ({
  part,
  quoteRequestLine,
  hasChildren,
  disablePartTypeChip = false,
  onSubmitPartDimensions,
  isConsumable,
  direction = "horizontal",
  showWeight = false,
  showMaterial = true,
}: Props) => {
  const { customMaterials } = useCustomMaterials({});

  const Container = direction === "horizontal" ? Group : Stack;

  const materialLayout = quoteRequestLine.layout?.materialLayout;

  const allCustomMaterialSubTypes = useMemo(
    () =>
      new Set(
        customMaterials.map((material) => material.subType).filter((subType) => subType !== null),
      ),
    [customMaterials],
  );
  const disablePartTypeChips = disablePartTypeChip || hasChildren;

  return (
    <Container gap="xxs" onClick={(e) => e.stopPropagation()}>
      <DimensionChip
        part={part}
        quoteRequestLine={quoteRequestLine}
        updatable={true}
        onSubmitPartDimensions={onSubmitPartDimensions}
      />
      {showWeight && materialLayout && quoteRequestLine.materialGrade && (
        <WeightChip
          part={part}
          density={materialLayout.density}
          quoteRequestLineId={quoteRequestLine.id}
          materialGrade={quoteRequestLine.materialGrade}
        />
      )}
      {showMaterial && !isConsumable && (
        <MaterialChip materialGrade={quoteRequestLine.materialGrade} />
      )}
      <PartTypeChip
        quoteRequestLine={quoteRequestLine}
        part={part}
        allSubTypes={Array.from(allCustomMaterialSubTypes)}
        disabled={disablePartTypeChips}
      />
    </Container>
  );
};
